/**
 * Builds the Excel/Word/PDF export for the Creditors register.
 *
 * The on-screen table is the source of truth — not `summarizationTasks.output`,
 * which is a counts-and-narrative dump. The export is the unfiltered, ungrouped
 * register: one row per active creditor, matching the table columns.
 *
 * Money is written as a real number so the practitioner can sort / sum in Excel.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import {
  POD_STATUS_LABELS,
  derivePodOutcome,
  type PodClaimStatus,
  type PodStatusRow,
} from "@/components/jobs/phases/recoveries/podShared";
import { RANKING_LABELS, RANKING_SORT_ORDER, creditorRanking } from "./ranking";
import type { LiabilityEntryUI } from "./types";

const SOURCE_LABELS: Record<string, string> = {
  aged_payables: "Aged payables",
  contract: "Contract",
  director_disclosure: "Director disclosure",
  invoice: "Invoice",
  ledger: "Ledger",
  bank_statement: "Bank statement",
  proof_of_debt: "Proof of Debt",
  other: "Other",
};

const NATURE_LABELS: Record<string, string> = {
  trade: "Trade",
  lease: "Lease",
  tax: "Tax",
  utility: "Utility",
  professional_fees: "Prof. fees",
  loan: "Loan",
  insurer: "Insurer",
  other: "Other",
};

function podReceivedLabel(status?: PodStatusRow): string {
  if (!status) return "";
  const claimStatus = status.status as PodClaimStatus;
  const received = claimStatus === "received" || claimStatus === "adjudicated";
  const label = received
    ? "Received"
    : (POD_STATUS_LABELS[claimStatus] ?? claimStatus);
  return status.matchedByName ? `${label} (matched by name)` : label;
}

function podAdjudicatedLabel(status?: PodStatusRow): string {
  if (!status) return "";
  return derivePodOutcome(status)?.label ?? "Pending";
}

function confidenceLabel(confidence: string): string {
  if (!confidence) return "";
  return confidence.charAt(0).toUpperCase() + confidence.slice(1);
}

function creditorsSheet(
  entries: LiabilityEntryUI[],
  podStatusByEntry: Map<string, PodStatusRow>,
): ExportSheet {
  // Excluded creditors are omitted from the register export (aligns with the
  // panel totals, POD register, and reporting).
  const included = entries.filter((e) => !e.isExcluded);
  const sorted = [...included].sort((a, b) => {
    const byRank =
      RANKING_SORT_ORDER[creditorRanking(a)] -
      RANKING_SORT_ORDER[creditorRanking(b)];
    if (byRank !== 0) return byRank;
    return b.amount - a.amount;
  });

  return {
    name: "Creditors",
    headers: [
      "Creditor",
      "Est Owed",
      "Source",
      "Ranking",
      "Type",
      "Confidence",
      "POD Received",
      "POD Adjudicated",
    ],
    rows: sorted.map((entry) => {
      const pod = podStatusByEntry.get(entry._id);
      const ranking = creditorRanking(entry);
      return [
        entry.creditorName,
        entry.amount,
        SOURCE_LABELS[entry.source] ?? entry.source,
        RANKING_LABELS[ranking],
        NATURE_LABELS[entry.natureOfDebt] ?? entry.natureOfDebt,
        confidenceLabel(entry.confidence),
        podReceivedLabel(pod),
        podAdjudicatedLabel(pod),
      ];
    }),
  };
}

export function buildCreditorsExport(
  entries: LiabilityEntryUI[],
  podStatuses: PodStatusRow[],
): ExportContent {
  const podStatusByEntry = new Map<string, PodStatusRow>();
  for (const row of podStatuses) {
    podStatusByEntry.set(row.liabilityEntryId, row);
  }

  return {
    title: "Creditors",
    sheets: [creditorsSheet(entries, podStatusByEntry)],
  };
}
